import { useRef, useCallback, useState, useEffect } from 'react'
// livekit-client is the app's heaviest dependency and only matters once a
// huddle is actually JOINED — imported lazily so it splits into its own chunk
// (most sessions never pay for it). `lk` caches the module; the enum users
// outside start() run only post-join, when the cache is guaranteed warm.
let lk = null
const loadLiveKit = async () => (lk ??= await import('livekit-client'))
import { getLivekitToken, postTranscriptChunk } from '../services/api'

export default function useWebRTC({ huddle, currentUser }) {
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoOn, setIsVideoOn] = useState(false)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [screenStream, setScreenStream] = useState(null)
  const [videoAvailable, setVideoAvailable] = useState(false)
  const [localStream, setLocalStream] = useState(null)
  const [remoteStreams, setRemoteStreams] = useState({})       // userId -> MediaStream (camera)
  const [remoteScreenStreams, setRemoteScreenStreams] = useState({}) // userId -> MediaStream (screen)
  const [connectionQualities, setConnectionQualities] = useState({}) // userId -> quality string
  const [activeSpeakers, setActiveSpeakers] = useState(new Set()) // set of speaking user identities
  const [isRecording, setIsRecording] = useState(false)

  const roomRef = useRef(null)
  const activeRef = useRef(false)
  const soloStreamRef = useRef(null)   // dev-only fallback when LiveKit isn't configured
  // Per-track recording refs.
  //
  // Model: SINGLE mic capture. Each participant records only their own local
  // mic, and the recorders feed off a CLONE of LiveKit's published mic track —
  // there is no second getUserMedia. Recording segments follow UNMUTED SPANS,
  // not join→leave: mute stops the recorders, flushes the session segment, and
  // stops the clone (with stopMicTrackOnMute LiveKit also stops its own track,
  // so the OS mic indicator goes dark — muted means the mic is RELEASED).
  // Unmute re-acquires the mic, clones the fresh track, and starts a new
  // segment (sessionIndex increments per span; the upload path already takes
  // a segment list).
  //
  //   captureRef               – { clone, stream } — the active clone of LiveKit's
  //                              mic track feeding both recorders. null while muted.
  //
  //   trackRecordersRef        – Map<key → { recorder, chunks, startedAt,
  //                                          userId, username, trackSid, sessionIndex }>
  //                              key = "local" (only local mic recorded)
  //
  //   completedTracksRef       – finished segments waiting to be uploaded on leave.
  //
  //   sessionCountRef          – Map<userId → count> tracks how many segments each
  //                              participant has recorded (for sessionIndex).
  const captureRef = useRef(null)
  const trackRecordersRef = useRef(new Map())
  const completedTracksRef = useRef([])
  const sessionCountRef = useRef(new Map())
  const liveTranscriptRef = useRef(null)  // { recorder, intervalId } for live 30s transcription segments
  // Synchronous mute state. React's `isMuted` state doesn't survive a
  // reconnect-triggered start() in time, so we mirror it here. start() reads
  // it to keep a reconnect-while-muted call muted (mic stays released, no
  // capture), and startSegment() reads it as defense in depth — while muted
  // the whole capture (and with it the transcript loop) doesn't exist.
  const isMutedRef = useRef(false)
  const muteBusyRef = useRef(false)  // serializes toggleMute's async transition
  // Stable stream refs stored on roomRef to avoid adding new hooks (which breaks HMR)
  if (!roomRef._stable) roomRef._stable = { cameras: {}, screens: {}, rebuildTimer: null, localStream: null }
  const stableRef = { current: roomRef._stable }

  // Build a MediaStream from the local participant's tracks (for HuddleBar VideoTile)
  // Reuses the same MediaStream object if tracks haven't changed (prevents VideoTile remount)
  function buildLocalStream(room) {
    if (!room?.localParticipant) return null
    const tracks = []
    for (const pub of room.localParticipant.trackPublications.values()) {
      // Only Camera tracks go into localStream — mic tracks have no place in a
    // <video> element's srcObject and cause a black flash when muted (LiveKit
    // stops the mic MediaStreamTrack, the browser sees an ended track in the
    // stream and briefly interrupts video rendering).
    if (pub.track && pub.source === lk.Track.Source.Camera) {
        tracks.push(pub.track.mediaStreamTrack)
      }
    }
    if (tracks.length === 0) { stableRef.current.localStream = null; return null }

    const prev = stableRef.current.localStream
    if (prev) {
      const prevTrackIds = new Set(prev.getTracks().map(t => t.id))
      const newTrackIds = new Set(tracks.map(t => t.id))
      // Same tracks — return the same object
      if (prevTrackIds.size === newTrackIds.size && [...prevTrackIds].every(id => newTrackIds.has(id))) {
        return prev
      }
      // Tracks changed — update in-place
      for (const t of prev.getTracks()) {
        if (!newTrackIds.has(t.id)) prev.removeTrack(t)
      }
      for (const t of tracks) {
        if (!prevTrackIds.has(t.id)) prev.addTrack(t)
      }
      return prev
    }
    const stream = new MediaStream(tracks)
    stableRef.current.localStream = stream
    return stream
  }

  // Rebuild remote streams from room participants (debounced to avoid rapid fire)
  // 150ms debounce — 50ms was fine for 2 people but at 10+ participants joining together
  // many events fire in quick succession; the extra headroom lets them batch into one rebuild.
  function rebuildRemoteStreams(room) {
    if (!room) return
    if (stableRef.current.rebuildTimer) clearTimeout(stableRef.current.rebuildTimer)
    stableRef.current.rebuildTimer = setTimeout(() => {
      _doRebuildRemoteStreams(room)
    }, 150)
  }

  function _doRebuildRemoteStreams(room) {
    if (!room) return
    const prevCameras = stableRef.current.cameras
    const prevScreens = stableRef.current.screens
    const nextCameraIds = new Set()
    const nextScreenIds = new Set()

    for (const p of room.remoteParticipants.values()) {
      const userId = p.identity
      // Camera/mic tracks
      const camTracks = []
      const screenTracks = []
      for (const pub of p.trackPublications.values()) {
        if (!pub.track || !pub.isSubscribed) continue
        if (pub.source === lk.Track.Source.ScreenShare || pub.source === lk.Track.Source.ScreenShareAudio) {
          // Both screen share video and audio go into the screen stream
          screenTracks.push(pub.track.mediaStreamTrack)
        } else if (pub.source === lk.Track.Source.Camera) {
          // Camera only — NOT Microphone. Audio is handled exclusively by LiveKit's
          // hidden <audio> elements (track.attach()). Including the mic track here
          // causes double audio: once through LiveKit's <audio>, once through the
          // <video> srcObject. This creates echo, interference, and audio dropouts.
          camTracks.push(pub.track.mediaStreamTrack)
        }
      }

      if (camTracks.length > 0) {
        nextCameraIds.add(userId)
        const existing = prevCameras[userId]
        if (existing) {
          // Update tracks in-place on the existing MediaStream (no remount)
          const currentTrackIds = new Set(existing.getTracks().map(t => t.id))
          const newTrackIds = new Set(camTracks.map(t => t.id))
          const tracksChanged = currentTrackIds.size !== newTrackIds.size ||
            [...newTrackIds].some(id => !currentTrackIds.has(id))
          // Remove tracks that are gone
          for (const t of existing.getTracks()) {
            if (!newTrackIds.has(t.id)) existing.removeTrack(t)
          }
          // Add new tracks
          for (const t of camTracks) {
            if (!currentTrackIds.has(t.id)) existing.addTrack(t)
          }
          // If tracks changed (e.g. video track added after audio), create a new MediaStream
          // wrapping the same underlying tracks. This gives a new object reference so the
          // setRemoteStreams anyChanged check triggers a React re-render, making VideoTile
          // show the video tile instead of staying on the audio-only placeholder.
          if (tracksChanged) prevCameras[userId] = new MediaStream(existing.getTracks())
        } else {
          prevCameras[userId] = new MediaStream(camTracks)
        }
      }

      if (screenTracks.length > 0) {
        nextScreenIds.add(userId)
        const existing = prevScreens[userId]
        if (existing) {
          const currentTrackIds = new Set(existing.getTracks().map(t => t.id))
          const newTrackIds = new Set(screenTracks.map(t => t.id))
          for (const t of existing.getTracks()) {
            if (!newTrackIds.has(t.id)) existing.removeTrack(t)
          }
          for (const t of screenTracks) {
            if (!currentTrackIds.has(t.id)) existing.addTrack(t)
          }
        } else {
          prevScreens[userId] = new MediaStream(screenTracks)
        }
      }
    }

    // Remove departed participants
    for (const userId of Object.keys(prevCameras)) {
      if (!nextCameraIds.has(userId)) delete prevCameras[userId]
    }
    for (const userId of Object.keys(prevScreens)) {
      if (!nextScreenIds.has(userId)) delete prevScreens[userId]
    }


    // Only trigger React re-render if participant set or stream objects actually changed
    setRemoteStreams(prev => {
      const prevKeys = Object.keys(prev).sort().join(',')
      const nextKeys = Object.keys(prevCameras).sort().join(',')
      if (prevKeys === nextKeys) {
        // Same participants — check if any MediaStream object changed
        const anyChanged = Object.keys(prevCameras).some(k => prev[k] !== prevCameras[k])
        if (!anyChanged) return prev
      }
      return { ...prevCameras }
    })
    setRemoteScreenStreams(prev => {
      const prevKeys = Object.keys(prev).sort().join(',')
      const nextKeys = Object.keys(prevScreens).sort().join(',')
      if (prevKeys === nextKeys) {
        const anyChanged = Object.keys(prevScreens).some(k => prev[k] !== prevScreens[k])
        if (!anyChanged) return prev
      }
      return { ...prevScreens }
    })
  }

  // Use refs so start() always reads the latest huddle/currentUser without
  // being recreated (which would trigger the join effect in HuddleBar)
  const huddleRef = useRef(huddle)
  const currentUserRef = useRef(currentUser)
  huddleRef.current = huddle
  currentUserRef.current = currentUser

  // Self-ref so the Disconnected event handler can call start() for auto-restart
  // (start is defined after the event setup, so we forward-declare it via ref)
  const startRef = useRef(null)

  const start = useCallback(async () => {
    const huddle = huddleRef.current
    const currentUser = currentUserRef.current
    if (!huddle || !currentUser) return
    if (activeRef.current) return

    activeRef.current = true

    try {
      // 0. Load LiveKit (its own chunk; cached after the first join)
      const { Room, RoomEvent, Track, ConnectionQuality } = await loadLiveKit()
      // 1. Get token from backend
      const res = await getLivekitToken(huddle.id)
      const { token, url } = res

      // 2. Create room with noise cancellation + adaptive streaming
      const room = new Room({
        audioCaptureDefaults: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
        videoCaptureDefaults: {
          resolution: { width: 1920, height: 1080, frameRate: 30 },
        },
        publishDefaults: {
          simulcast: true,          // send multiple quality layers (high/medium/low)
          videoCodec: 'h264',       // sharper than VP8 at same bitrate, hardware-accelerated
          videoEncoding: { maxBitrate: 3_000_000, maxFramerate: 30 },  // 3 Mbps cap
        },
        // Mute must RELEASE the mic, not just silence it: stops LiveKit's mic
        // MediaStreamTrack on mute (OS/browser mic indicator goes dark) and
        // re-acquires on unmute. Our recorders run on a clone that we stop
        // ourselves in toggleMute — see _stopCapture.
        stopMicTrackOnMute: true,
        adaptiveStream: false,      // disabled — causes blackouts when switching layers
        dynacast: false,            // disabled — with adaptiveStream off, dynacast pauses all simulcast layers when 5+ participants join because no subscriber sends quality signals, causing video to disappear
        reconnectPolicy: {
          maxRetries: 10,
          nextRetryDelayInMs: (context) => Math.min(1000 * Math.pow(2, context.retryCount), 10000),
        },
      })
      roomRef.current = room

      // 3. Set up event listeners BEFORE connecting
      room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        // Explicitly attach remote audio tracks so they play through LiveKit's hidden <audio> elements.
        // room.startAudio() only covers tracks subscribed at call-time; late-joiners need manual attach.
        if (track.kind === 'audio') {
          // Detach first to clear any stale <audio> elements from a prior subscribe cycle
          // (e.g. participant briefly disconnected and resubscribed). Without this, each
          // resubscribe adds another hidden <audio> element → doubled/echoing audio.
          track.detach()
          track.attach()
          // No remote recording here — each participant records their own local mic only.
          // Their audio is uploaded as a personal/group SDK node when they leave the call.
        }
        rebuildRemoteStreams(room)
      })
      room.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
        // Detach to clean up hidden <audio> elements
        track.detach()
        rebuildRemoteStreams(room)
      })
      // TrackMuted/TrackUnmuted do NOT change stream composition — no rebuild needed.
      // Rebuilding on mute forced a new MediaStream object reference, which caused
      // VideoTile to reassign srcObject mid-playback → visible black frame flash.
      room.on(RoomEvent.ParticipantConnected, (participant) => {
        rebuildRemoteStreams(room)
      })
      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        rebuildRemoteStreams(room)
      })
      // Only rebuild localStream for camera track changes — mic publish/unpublish is irrelevant
      // to the video element and rebuilding on mic events causes unnecessary renders/flicker.
      room.on(RoomEvent.LocalTrackPublished, (pub) => {
        if (pub.source === Track.Source.Camera) setLocalStream(buildLocalStream(room))
      })
      room.on(RoomEvent.LocalTrackUnpublished, (pub) => {
        if (pub.source === Track.Source.Camera) setLocalStream(buildLocalStream(room))
      })
      room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        setConnectionQualities(prev => ({
          ...prev,
          [participant.identity]: quality
        }))
      })
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        setActiveSpeakers(new Set(speakers.map(s => s.identity)))
      })
      room.on(RoomEvent.Reconnecting, () => {
        // Don't clear streams — keep last frame visible during brief reconnect
      })
      room.on(RoomEvent.Reconnected, () => {
        // Re-attach all remote audio tracks — the browser may have suspended them during
        // the connection blip. Detach first to avoid accumulating duplicate <audio> elements.
        for (const p of room.remoteParticipants.values()) {
          for (const pub of p.trackPublications.values()) {
            if (pub.track?.kind === 'audio' && pub.isSubscribed) {
              pub.track.detach()
              pub.track.attach()
            }
          }
        }
        // Resume audio context if browser suspended it during the blip
        room.startAudio().catch(() => {})
        rebuildRemoteStreams(room)
        setLocalStream(buildLocalStream(room))
      })
      room.on(RoomEvent.Disconnected, (reason) => {
        // Only clear state if this was an intentional disconnect (stop() sets activeRef false first).
        // If activeRef is still true the disconnect was unexpected (ICE failure, server drop, etc.)
        // — attempt a full restart so the user stays in the call without needing to rejoin manually.
        if (activeRef.current) {
          console.warn('[LiveKit] Unexpected disconnect — scheduling restart in 1.5s')
          roomRef.current = null
          setTimeout(() => {
            if (activeRef.current) {
              // Reset room._stable so stale streams don't leak into the new connection
              roomRef._stable = { cameras: {}, screens: {}, rebuildTimer: null, localStream: null }
              activeRef.current = false // let start() re-enter
              startRef.current?.()
            }
          }, 1500)
        } else {
          activeRef.current = false
        }
      })

      // 4. Connect
      await room.connect(url, token)

      // 4b. Start audio playback. Wrapped in its own try/catch — on auto-rejoin
      // (page reload while already in a huddle) there is no prior user gesture so
      // Chrome's autoplay policy blocks play(). This must NOT abort start() because
      // the call itself is fine; audio will resume on the next user interaction
      // (e.g. clicking mute/unmute, which counts as a gesture and re-enables audio).
      try {
        await room.startAudio()
      } catch (audioErr) {
        console.warn('[LiveKit] startAudio blocked by autoplay policy — audio will resume on first user gesture:', audioErr.message)
      }

      // 5. Publish mic (camera off by default, same as before). On a
      // reconnect-triggered restart, honor the mute state from before the
      // drop: stay muted, mic stays released, no capture. (Previously a
      // reconnect-while-muted unmuted LiveKit but kept recording silent —
      // others could hear you while "muted".)
      await room.localParticipant.setMicrophoneEnabled(!isMutedRef.current)
      setIsMuted(isMutedRef.current)

      // Check if camera is available
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        setVideoAvailable(devices.some(d => d.kind === 'videoinput'))
      } catch {
        setVideoAvailable(false)
      }

      setLocalStream(buildLocalStream(room))
      rebuildRemoteStreams(room)

      // ── Per-track recording ──────────────────────────────────────────────────
      // SINGLE CAPTURE: both recorders run on a clone of LiveKit's own mic
      // track (no second getUserMedia — one OS capture, one permission, no
      // AGC/echo fights between parallel captures). While muted there is
      // nothing to clone and nothing to record.
      if (!isMutedRef.current) _startCapture(huddle.id)
      setIsRecording(true)

    } catch (err) {
      console.error('[LiveKit] Start failed:', err)
      // ── DEV SOLO FALLBACK ────────────────────────────────────────────
      // If the backend has no LiveKit creds configured, token POST 500s
      // and the room never connects. In dev we fall back to a local-only
      // getUserMedia stream so you can at least preview your own video
      // tile in the huddle UI without signing up for LiveKit.
      if (import.meta.env.DEV) {
        try {
          console.warn('[LiveKit] Falling back to dev solo mode (no LiveKit connection) — local camera/mic only.')
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: { width: 1280, height: 720 },
          })
          soloStreamRef.current = stream
          const videoTracks = stream.getVideoTracks()
          // Start with camera OFF so the placeholder shows — user can toggle on.
          videoTracks.forEach(t => { t.enabled = false })
          setLocalStream(stream)
          setVideoAvailable(videoTracks.length > 0)
          setIsMuted(false)
          setIsVideoOn(false)
          activeRef.current = true
          return
        } catch (mediaErr) {
          console.error('[LiveKit] Dev solo fallback also failed (getUserMedia):', mediaErr)
        }
      }
      activeRef.current = false
    }
  }, []) // stable — reads huddle/currentUser from refs

  // Keep self-ref up to date so the Disconnected handler can call start() for auto-restart
  startRef.current = start

  const stop = useCallback(async () => {
    activeRef.current = false
    // Clear UI immediately so the REC indicator drops the moment leave is
    // initiated, not after the per-track blob flush completes (~hundreds of ms).
    setIsRecording(false)
    // Leaving resets mute — the next join starts unmuted (matching start()'s
    // fresh-join behavior; only reconnects preserve mute).
    isMutedRef.current = false
    if (stableRef.current.rebuildTimer) { clearTimeout(stableRef.current.rebuildTimer); stableRef.current.rebuildTimer = null }

    // Stop transcription + flush the session segment + release the mic clone.
    // Awaited so blobs are fully flushed before HuddleBar calls
    // getTrackRecordings(). If this is skipped (tab closed without firing
    // stop), the pagehide listener at the bottom of this hook is the safety net.
    await _stopCapture()

    const room = roomRef.current
    if (room) {
      room.disconnect()
      roomRef.current = null
    }
    // Dev solo mode cleanup
    if (soloStreamRef.current) {
      soloStreamRef.current.getTracks().forEach(t => t.stop())
      soloStreamRef.current = null
    }
    roomRef._stable = { cameras: {}, screens: {}, rebuildTimer: null, localStream: null }
    setLocalStream(null)
    setRemoteStreams({})
    setRemoteScreenStreams({})
    setScreenStream(null)
    setIsVideoOn(false)
    setIsMuted(false)
    setIsScreenSharing(false)
    setVideoAvailable(false)
    setConnectionQualities({})
    setActiveSpeakers(new Set())
  }, [])

  // ── Single-capture lifecycle ─────────────────────────────────────────────────
  // The capture exists exactly while the user is unmuted in a connected room.
  // Mute → _stopCapture (flush + send + release); unmute → _startCapture on the
  // freshly re-acquired LiveKit track.

  function _startCapture(huddleId, attempt = 0) {
    if (captureRef.current) return
    // The user may have muted or left during a retry window — stand down.
    if (isMutedRef.current || !activeRef.current) return
    const pub = roomRef.current?.localParticipant?.getTrackPublication?.(lk.Track.Source.Microphone)
    const srcTrack = pub?.track?.mediaStreamTrack
    if (!srcTrack || srcTrack.readyState === 'ended') {
      // LiveKit can resolve setMicrophoneEnabled(true) a beat before the
      // re-acquired track lands on the publication. Retry briefly — giving up
      // here would silently record nothing for the whole unmuted span.
      if (attempt < 5) {
        setTimeout(() => _startCapture(huddleId, attempt + 1), 200)
        return
      }
      console.warn('[TrackRec] No live mic track to capture from — recording skipped for this span')
      return
    }
    // Clone: recording must not disturb the published track, and LiveKit
    // stopping its track (stopMicTrackOnMute, reconnects) must not kill an
    // in-flight flush. We stop the clone ourselves in _stopCapture.
    const clone = srcTrack.clone()
    const stream = new MediaStream([clone])
    captureRef.current = { clone, stream }
    // Safety net: device unplugged / source died — flush what we have.
    clone.onended = () => { _stopCapture() }
    const user = currentUserRef.current
    _startTrackRecording('local', user.id, user.username, 'local', clone)
    _startLiveTranscription(stream, huddleId)
  }

  async function _stopCapture() {
    const cap = captureRef.current
    if (!cap) return
    captureRef.current = null
    // Stop the transcript segment first — its onstop sends the pre-mute
    // portion (spoken unmuted; the session segment holds it too).
    _stopLiveTranscription()
    // Flush the session segment for this unmuted span into completedTracksRef.
    await _stopTrackRecording('local')
    // Release the clone — with LiveKit's own track stopped on mute, this is
    // what turns the OS mic indicator off.
    cap.clone.stop()
  }

  // ── Live transcription loop ──────────────────────────────────────────────────
  // Every 30s: stop the current recorder (flushes a complete .webm), send to
  // /transcript-chunk, then immediately start a fresh recorder on the same stream.
  // Each window is a self-contained webm — the transcriber can decode it standalone.
  //
  // The loop only exists while an unmuted capture is active (see _startCapture /
  // _stopCapture). Muting stops the whole loop — the final segment is cut at the
  // click and SENT, since it holds only pre-mute speech (spoken unmuted, and in
  // the session recording too). Muted spans are therefore never posted: they
  // used to go up as silence, and Whisper-family models hallucinate text on
  // silence, minting transcript nodes for muted users. Unmuting starts a fresh
  // loop immediately. The backend keeps its own energy gate as a second line of
  // defense (unmuted-but-silent chunks).

  function _startLiveTranscription(stream, huddleId) {
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus' : 'audio/webm'

    function startSegment() {
      // Defense in depth — the capture shouldn't exist while muted at all.
      if (isMutedRef.current) return null
      const chunks = []
      const ms = new MediaStream(stream.getAudioTracks())
      const recorder = new MediaRecorder(ms, { mimeType })
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: mimeType })
        if (blob.size < 500) return  // too small to contain speech
        try {
          await postTranscriptChunk(huddleId, blob)
        } catch (err) {
          console.warn('[LiveTranscript] send failed:', err)
        }
      }
      recorder.start()
      return recorder
    }

    let current = startSegment()
    const intervalId = setInterval(() => {
      if (current?.state === 'recording') current.stop()
      current = startSegment()
      liveTranscriptRef.current = { recorder: current, intervalId }
    }, 30_000)
    liveTranscriptRef.current = { recorder: current, intervalId }
  }

  function _stopLiveTranscription() {
    const lt = liveTranscriptRef.current
    if (!lt) return
    clearInterval(lt.intervalId)
    if (lt.recorder?.state === 'recording') lt.recorder.stop()
    liveTranscriptRef.current = null
  }

  // ── Per-track recorder helpers ───────────────────────────────────────────────

  function _startTrackRecording(key, userId, username, trackSid, audioTrack) {
    if (trackRecordersRef.current.has(key)) return
    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm'
      const recorder = new MediaRecorder(new MediaStream([audioTrack]), { mimeType })
      const chunks = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      recorder.start(1000)
      const sessionIndex = sessionCountRef.current.get(userId) || 0
      sessionCountRef.current.set(userId, sessionIndex + 1)
      trackRecordersRef.current.set(key, {
        recorder, chunks, sessionIndex,
        userId, username, trackSid,
        startedAt: new Date().toISOString(),
      })
    } catch (err) {
      console.warn(`[TrackRec] Could not start recorder for key=${key}:`, err)
    }
  }

  async function _stopTrackRecording(key) {
    const entry = trackRecordersRef.current.get(key)
    if (!entry) return
    trackRecordersRef.current.delete(key)
    if (entry.recorder.state !== 'inactive') {
      await new Promise(resolve => { entry.recorder.onstop = resolve; entry.recorder.stop() })
    }
    const blob = new Blob(entry.chunks, { type: 'audio/webm' })
    if (blob.size > 0) {
      completedTracksRef.current.push({
        userId: entry.userId,
        username: entry.username,
        trackSid: entry.trackSid,
        sessionIndex: entry.sessionIndex,
        blob,
        startedAt: entry.startedAt,
        endedAt: new Date().toISOString(),
      })
    } else {
      console.warn(`[TrackRec] Empty blob for key=${key} — getUserMedia may have failed or call was too short`)
    }
  }

  /** Returns all completed track segments and clears the buffer. Call after stop(). */
  const getTrackRecordings = useCallback(() => {
    const segments = completedTracksRef.current
    completedTracksRef.current = []
    sessionCountRef.current = new Map()
    return segments
  }, [])

  const toggleMute = useCallback(async () => {
    // Dev solo mode: no LiveKit room — just flip the local track.
    if (!roomRef.current && soloStreamRef.current) {
      const newMuted = !isMuted
      soloStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !newMuted })
      isMutedRef.current = newMuted   // keep the ref honest (solo mode runs no transcription loop)
      setIsMuted(newMuted)
      return true
    }
    const room = roomRef.current
    if (!room || !activeRef.current) return false
    // Serialize toggles: a click during the async transition reads stale
    // `isMuted` and repeats the SAME direction (losing the user's intent),
    // while HuddleBar's toggleHuddleMute flips the server-side flag per
    // click — local and remote mute state would diverge. Drop the click.
    if (muteBusyRef.current) return false
    muteBusyRef.current = true
    const newMuted = !isMuted

    // Set the mute flag IMMEDIATELY (before any awaits): startSegment() reads
    // it, so no new transcript segment can be created for the muted span even
    // if the 30s tick fires during this function's await window.
    isMutedRef.current = newMuted

    // ONE control, one promise: muted means nothing is heard, recorded, or
    // transcribed — and the mic is RELEASED (OS indicator off). The final
    // transcript segment is cut at the click and sent (pre-mute speech only;
    // the session segment flushed here holds the same audio).
    try {
      if (newMuted) {
        await _stopCapture()
        // stopMicTrackOnMute makes this stop LiveKit's own mic track too.
        await room.localParticipant.setMicrophoneEnabled(false)
      } else {
        // Re-acquire the mic first, then clone the fresh track for recording.
        await room.localParticipant.setMicrophoneEnabled(true)
        _startCapture(huddleRef.current?.id)
      }
      setIsMuted(newMuted)
      return true
    } catch (err) {
      // Revert — a flag pointing one way while LiveKit points the other is
      // worse than a failed toggle (e.g. "muted" while the mic transmits).
      console.error('[LiveKit] toggleMute failed — reverting:', err)
      isMutedRef.current = !newMuted
      if (newMuted && !captureRef.current) {
        // Mute path stopped the capture before LiveKit failed; resume it
        // (a fresh gap-segment — the flushed one stays valid).
        _startCapture(huddleRef.current?.id)
      }
      return false
    } finally {
      muteBusyRef.current = false
    }
  }, [isMuted])

  const toggleVideo = useCallback(async () => {
    // Dev solo mode: no LiveKit room — flip the video track on the
    // local getUserMedia stream and refresh the state.
    if (!roomRef.current && soloStreamRef.current) {
      const newVideoOn = !isVideoOn
      soloStreamRef.current.getVideoTracks().forEach(t => { t.enabled = newVideoOn })
      setIsVideoOn(newVideoOn)
      // Force a new MediaStream reference so VideoTile re-evaluates
      // its hasVideoTrack check and swaps the placeholder.
      setLocalStream(new MediaStream(soloStreamRef.current.getTracks()))
      return
    }
    const room = roomRef.current
    if (!room || !activeRef.current) return
    const newVideoOn = !isVideoOn
    // Update isVideoOn BEFORE the async camera toggle so the tile transitions
    // to placeholder immediately — prevents the black frame that appears when
    // the camera track is unpublished but isVideoOn is still true.
    setIsVideoOn(newVideoOn)
    try {
      await room.localParticipant.setCameraEnabled(newVideoOn)
      setLocalStream(buildLocalStream(room))
    } catch (err) {
      setIsVideoOn(!newVideoOn) // revert on failure
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        console.warn('[LiveKit] Camera permission denied — check browser settings')
        setVideoAvailable(false)
      } else {
        console.error('[LiveKit] toggleVideo failed:', err)
      }
    }
  }, [isVideoOn])

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current
    if (!room || !activeRef.current) return

    if (isScreenSharing) {
      await room.localParticipant.setScreenShareEnabled(false)
      setIsScreenSharing(false)
      setScreenStream(null)
    } else {
      try {
        await room.localParticipant.setScreenShareEnabled(true, { audio: true })
        setIsScreenSharing(true)
        // Get the screen share track for local preview
        for (const pub of room.localParticipant.trackPublications.values()) {
          if (pub.source === lk.Track.Source.ScreenShare && pub.track) {
            setScreenStream(new MediaStream([pub.track.mediaStreamTrack]))
            break
          }
        }

        // Listen for browser's native "Stop sharing" button
        for (const pub of room.localParticipant.trackPublications.values()) {
          if (pub.source === lk.Track.Source.ScreenShare && pub.track) {
            pub.track.mediaStreamTrack.onended = () => {
              room.localParticipant.setScreenShareEnabled(false)
              setIsScreenSharing(false)
              setScreenStream(null)
            }
            break
          }
        }
      } catch (err) {
      }
    }
  }, [isScreenSharing])

  // Auto-reconnect when participants change (LiveKit handles this internally,
  // but we rebuild streams to keep React state in sync)
  useEffect(() => {
    const room = roomRef.current
    if (!room || !activeRef.current) return
    rebuildRemoteStreams(room)
  }, [JSON.stringify(huddle?.participants?.map(p => p.user_id))])

  // Check camera availability on mount (not just inside start()) so the button
  // shows immediately after page reload, before the user re-joins the call.
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(devices => setVideoAvailable(devices.some(d => d.kind === 'videoinput')))
      .catch(() => {})
  }, [])

  // Cleanup on unmount
  useEffect(() => { return () => stop() }, [stop])

  // Safety net: release the recording mic stream and stop any active recorders
  // when the tab is hidden/closed. Without this, closing the tab while in a
  // call leaves the browser's tab mic indicator (red dot) lit and — worse —
  // the OS still sees an active mic capture until the page is fully torn down.
  // pagehide fires reliably on tab close, navigation, and bfcache eviction.
  useEffect(() => {
    function releaseRecordingMic() {
      try {
        // Stop both MediaRecorders so they don't keep buffering after pagehide
        const lt = liveTranscriptRef.current
        if (lt?.recorder?.state === 'recording') { try { lt.recorder.stop() } catch {} }
        if (lt?.intervalId) clearInterval(lt.intervalId)
        liveTranscriptRef.current = null
        for (const entry of trackRecordersRef.current.values()) {
          if (entry.recorder?.state !== 'inactive') { try { entry.recorder.stop() } catch {} }
        }
        // Release the mic clone — LiveKit's own track dies with the page, but
        // an unreleased clone would keep the OS capture alive until teardown.
        if (captureRef.current) {
          try { captureRef.current.clone.stop() } catch {}
          captureRef.current = null
        }
      } catch {}
    }
    window.addEventListener('pagehide', releaseRecordingMic)
    return () => window.removeEventListener('pagehide', releaseRecordingMic)
  }, [])

  return {
    start, stop, toggleMute, isMuted,
    toggleVideo, isVideoOn, videoAvailable,
    toggleScreenShare, isScreenSharing, screenStream,
    localStream,
    remoteStreams,
    remoteScreenStreams,
    connectionQualities,
    getTrackRecordings,
    isRecording,
    activeSpeakers,
  }
}
